import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getJupidEmbedToken } from "../../api/taxes/taxesServices";

const JUPID_PARTNER_ID = "halper";
const JUPID_APP_URL = "https://app.jupid.com";
const TOKEN_REFRESH_MS = 4 * 60 * 1000;

function loadJupidScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector("script[data-jupid-sdk]")) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.jupidSdk = "true";
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load Jupid SDK"));
    document.body.appendChild(script);
  });
}

export default function Taxes() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const tokenRef = useRef(null);
  const cancelledRef = useRef(false);
  const intervalRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    cancelledRef.current = false;

    const refreshToken = async () => {
      const token = await getJupidEmbedToken();
      if (cancelledRef.current) return;
      tokenRef.current = token;

      if (typeof window.JupidEmbed?.updateToken === "function") {
        window.JupidEmbed.updateToken(token);
      } else if (typeof window.JupidEmbed?.update === "function") {
        window.JupidEmbed.update({ token });
      } else {
        try { window.JupidEmbed?.unmount?.(); } catch {}
        window.JupidEmbed.mount({
          partnerId: JUPID_PARTNER_ID,
          token,
          container: containerRef.current,
          shell: "topbar",
          chat: "disabled",
          appUrl: JUPID_APP_URL,
        });
      }
    };

    const init = async () => {
      try {
        const token = await getJupidEmbedToken();
        if (cancelledRef.current) return;
        tokenRef.current = token;

        await loadJupidScript(`${JUPID_APP_URL}/embed.js`);
        if (cancelledRef.current) return;

        window.JupidEmbed.mount({
          partnerId: JUPID_PARTNER_ID,
          token: tokenRef.current,
          container: containerRef.current,
          initialPath: "/",
          shell: "topbar",
          chat: "disabled",
          appUrl: JUPID_APP_URL,
        });

        if (!cancelledRef.current) setStatus("ready");

        intervalRef.current = setInterval(async () => {
          if (cancelledRef.current) return;
          try {
            await refreshToken();
          } catch (err) {
            console.error("Jupid token refresh failed:", err);
          }
        }, TOKEN_REFRESH_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        console.error("Jupid embed error:", err);
        setErrorMessage(err?.response?.data?.detail || err.message || "Failed to load Taxes");
        setStatus("error");
      }
    };

    init();

    return () => {
      cancelledRef.current = true;
      clearInterval(intervalRef.current);
      try { window.JupidEmbed?.unmount?.(); } catch {}
    };
  }, []);

  return (
    <div className="flex flex-col h-full w-full max-w-full overflow-x-hidden">
      {status === "loading" && (
        <div className="flex items-center justify-center flex-1">
          <span className="text-neutral-400 text-sm">Loading Taxes...</span>
        </div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center justify-center flex-1 p-6 text-center max-w-md mx-auto">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t("sidebar.accounting")}
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            {t("sidebar.accountingUnavailable")}
          </p>
          {errorMessage && (
            <div className="w-full text-left bg-gray-50 border border-gray-100 rounded-xl p-4 mt-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                {t("sidebar.accountingErrorDetails")}
              </div>
              <div className="text-xs text-gray-600 font-mono break-all">
                {errorMessage}
              </div>
            </div>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        id="jupid-embed"
        style={{
          height: "100%",
          minHeight: 720,
          width: "100%",
          maxWidth: "100%",
          display: status === "ready" ? "block" : "none",
        }}
      />
    </div>
  );
}
